/**
 * Computes the column layout for a list of events that may overlap in time.
 * This mimics how Google Calendar / Outlook lay out concurrent events side-by-side.
 *
 * Each event in the input must have `id`, `startTime`, and `endTime` (HH:mm strings).
 *
 * Returns a map of id → { columnIndex, totalColumns }
 *  - columnIndex: which column this event belongs to (0-based)
 *  - totalColumns: how many columns exist in this event's overlap group
 */
export interface ITimedEvent {
  id: string;
  startTime: string;
  endTime: string;
}

export interface IColumnLayout {
  columnIndex: number;
  totalColumns: number;
}

const timeToMinutes = (time: string): number => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

const overlaps = (a: ITimedEvent, b: ITimedEvent): boolean => {
  return timeToMinutes(a.startTime) < timeToMinutes(b.endTime)
    && timeToMinutes(a.endTime) > timeToMinutes(b.startTime);
};

export const computeColumnLayout = (events: ITimedEvent[]): Map<string, IColumnLayout> => {
  const result = new Map<string, IColumnLayout>();

  // Step 1: build overlap groups (clusters)
  // Each cluster is a set of event indices that are all reachable via overlaps
  const visited = new Set<number>();
  const clusters: number[][] = [];

  for (let i = 0; i < events.length; i++) {
    if (visited.has(i)) continue;

    // BFS to find all events connected to i through overlaps
    const cluster: number[] = [];
    const queue = [i];
    while (queue.length > 0) {
      const cur = queue.shift()!;
      if (visited.has(cur)) continue;
      visited.add(cur);
      cluster.push(cur);
      for (let j = 0; j < events.length; j++) {
        if (!visited.has(j) && overlaps(events[cur], events[j])) {
          queue.push(j);
        }
      }
    }
    clusters.push(cluster);
  }

  // Step 2: within each cluster, assign columns greedily
  for (const cluster of clusters) {
    // columns[c] = end time (minutes) of last event placed in column c
    const columns: number[] = [];

    // Sort by start time within cluster
    const sorted = [...cluster].sort(
      (a, b) => timeToMinutes(events[a].startTime) - timeToMinutes(events[b].startTime)
    );

    const eventColumn = new Map<number, number>();

    for (const idx of sorted) {
      const event = events[idx];
      const startMin = timeToMinutes(event.startTime);

      // Find the first column where the last event has already ended
      let placed = false;
      for (let c = 0; c < columns.length; c++) {
        if (columns[c] <= startMin) {
          columns[c] = timeToMinutes(event.endTime);
          eventColumn.set(idx, c);
          placed = true;
          break;
        }
      }
      if (!placed) {
        eventColumn.set(idx, columns.length);
        columns.push(timeToMinutes(event.endTime));
      }
    }

    const totalColumns = columns.length;

    for (const idx of cluster) {
      result.set(events[idx].id, {
        columnIndex: eventColumn.get(idx)!,
        totalColumns,
      });
    }
  }

  return result;
};
